import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { useAtom } from "jotai";
import { Lock, User, Wrench } from "lucide-react";

import { authenticationAtom } from "@/State";
import { userAPI } from "@/UserAPI";
import { invalidatePhaseParcels } from "@/portfolio-dashboard/phaseParcelsApi";
import { forgetProjects } from "@/cbos/selectedProject";
import { configuration } from "@/Configuration";
import { UserMenu } from "@/components/arc/user-menu/user-menu";
import { useUserPhoto } from "@/components/UserMenu";

const LANGUAGES = [
  { value: "de", country: "de", labelKey: "header.german" },
  { value: "en", country: "gb", labelKey: "header.english" },
] as const;

/**
 * Das Kontomenü oben rechts (Avatar): Name und E-Mail-Adresse, Benutzer- und
 * Sicherheitsprofil, die Administration (wer sie sehen darf), der Sprachwechsel,
 * Abmelden und die Versionen von Portal und Server. Die Oberfläche ist das
 * User-Menü von uiarc (`components/arc/user-menu`), der Theme-Schalter dort ist
 * hier der Sprachschalter.
 */
export function AccountMenu() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [authentication, setAuthentication] = useAtom(authenticationAtom) as any;
  const [serverVersion, setServerVersion] = React.useState<string | null>(null);
  const userPhoto = useUserPhoto();
  const [language, setLanguage] = React.useState<string>(i18next.resolvedLanguage ?? "de");
  const fullName = [authentication?.firstName, authentication?.lastName].filter(Boolean).join(" ");

  React.useEffect(() => {
    fetch(`${configuration.serverUrl}/version`)
      .then((res) => res.json())
      .then((data) => setServerVersion(data.version))
      .catch(() => setServerVersion("unavailable"));
  }, []);

  // Gleiche Schritte wie der Logout im Header — der Header behält seine
  // Variante für die noch nicht migrierten Seiten.
  const logout = () => {
    const tenant = authentication?.customer;
    setAuthentication(null);
    invalidatePhaseParcels();
    forgetProjects();
    userAPI.logout();
    navigate(tenant ? `/login?tenant=${encodeURIComponent(tenant)}` : "/login");
  };

  const changeLanguage = (next: string) => {
    setLanguage(next);
    i18n.changeLanguage(next);
    localStorage.setItem("i18nextLng", next);
  };

  // Die Einstellungen stehen nicht hier: Der Weg dorthin ist "Zum Dashboard" in
  // der Seitenleiste des Chats.
  const items = [
    {
      label: t("settingsPanel.userProfile"),
      icon: <User size={16} strokeWidth={1.75} />,
      onSelect: () => navigate("/settings?panel=userProfile"),
    },
    {
      label: t("settingsPanel.securityProfile"),
      icon: <Lock size={16} strokeWidth={1.75} />,
      onSelect: () => navigate("/settings?panel=securityProfile"),
    },
    ...(configuration.shouldSeeAdministration(authentication)
      ? [
          {
            label: t("header.administration"),
            icon: <Wrench size={16} strokeWidth={1.75} />,
            onSelect: () => navigate("/administration"),
          },
        ]
      : []),
  ];

  return (
    <UserMenu
      user={{
        name: fullName || authentication?.email || "",
        email: authentication?.email ?? "",
        avatarSrc: userPhoto ?? undefined,
      }}
      items={items}
      showName
      showChevron={false}
      language={{
        label: t("header.changeLanguage"),
        value: language,
        onChange: changeLanguage,
        options: LANGUAGES.map((option) => ({
          value: option.value,
          label: t(option.labelKey),
          icon: <span aria-hidden className={`fi fi-${option.country}`} />,
        })),
      }}
      labels={{
        account: t("settingsPanel.userProfile"),
        signOut: t("header.logout"),
        signingOut: t("header.logout"),
      }}
      footer={
        <>
          <span>{`${t("header.version")} · Portal: ${configuration.version}`}</span>
          <span>{`Server: ${serverVersion ?? "..."}`}</span>
        </>
      }
      onSignOut={logout}
    />
  );
}
